import type { JobsDetailData, ProjectsUpdatePayload } from '@contracts'
import {
  WppIconAvailableCheckmark,
  WppIconDownload,
  WppIconDraft,
  WppIconError,
  WppIconPending,
  WppIconSuccess,
  WppIconSync,
  WppTag,
} from '@wppopen/components-library-react'
import type { VariantValidationStatusType } from 'components/VariantData/utils'
import type { ImportsCreateDataV5 } from 'utils/utils.d'
import styles from './StatusMessage.module.scss'

// controls what each status should display
export const STATUS = {
  DRAFT: {
    label: 'Draft',
    icon: <WppIconDraft data-testid="draft-status-icon" />,
    tag: <WppTag data-testid="status-tag-draft" label="Draft" variant="neutral" />,
  },
  IN_PROGRESS: {
    label: 'In progress',
    icon: <WppIconSync data-testid="ip-status-icon" />,
    tag: <WppTag data-testid="status-tag-in-progress" label="In progress" variant="neutral" />,
  },
  READY_FOR_REVIEW: {
    label: 'Ready for review',
    icon: <WppIconAvailableCheckmark data-testid="rfr-status-icon" />,
    tag: (
      <WppTag
        data-testid="status-tag-pending-approval"
        label="Pending approval"
        variant="neutral"
        className={styles.blue_tag}
      />
    ),
  },
  READY_TO_DOWNLOAD: {
    label: 'Ready for download',
    icon: <WppIconDownload data-testid="rtd-status-icon" />,
    tag: <WppTag data-testid="status-tag-ready-for-download" label="Ready for download" variant="positive" />,
  },
  SUBMITTED: {
    label: 'Submitted',
    icon: <WppIconSync data-testid="submitted-status-icon" />,
    tag: <WppTag data-testid="status-tag-submitted" label="Submitted" variant="neutral" className={styles.blue_tag} />,
  },
  COMPLETED: {
    label: 'Completed',
    icon: <WppIconSuccess data-testid="completed-status-icon" />,
    tag: <WppTag data-testid="status-tag-completed" label="Completed" variant="positive" />,
  },
  PROJECT_COMPLETED: {
    label: 'Generation Complete',
    icon: <WppIconSuccess data-testid="generation-complete-status-icon" />,
    tag: <WppTag data-testid="status-tag-generation-complete" label="Generation complete" variant="positive" />,
  },
  FAILED: {
    label: 'Failed',
    icon: <WppIconError data-testid="failed-status-icon" />,
    tag: <WppTag data-testid="status-tag-failed" label="Failed" variant="negative" />,
  },
  DOWNLOADED: {
    label: 'Downloaded',
    icon: <WppIconSuccess data-testid="downloaded-status-icon" />,
    tag: <WppTag data-testid="status-tag-downloaded" label="Downloaded" variant="positive" />,
  },
  PROCESSING: {
    label: 'Processing',
    icon: <WppIconPending data-testid="processing-status-icon" />,
    tag: <WppTag data-testid="status-tag-processing" label="Processing" variant="neutral" />,
  },
  BLOCKED: {
    label: 'Blocked',
    icon: <WppIconError data-testid="blocked-status-icon" />,
    tag: <WppTag data-testid="status-tag-blocked" label="Queued" variant="neutral" />,
  },
  EXECUTING: {
    label: 'Executing',
    icon: <WppIconSync data-testid="executing-status-icon" />,
    tag: <WppTag data-testid="status-tag-executing" label="Executing" variant="neutral" />,
  },
  IMPORTING: {
    label: 'Importing',
    icon: <WppIconSync data-testid="importing-status-icon" />,
    tag: <WppTag data-testid="status-tag-importing" label="Importing" variant="neutral" />,
  },
  ERROR: {
    label: 'Something went wrong',
    icon: undefined,
    tag: <WppTag data-testid="status-tag-unknown" label="Unknown" variant="warning" />,
  },
  INCOMPLETE: {
    label: 'Incomplete',
    icon: undefined,
    tag: <WppTag data-testid="status-tag-incomplete" label="Incomplete" variant="warning" />,
  },
  COMPLETE: {
    label: 'Good',
    icon: undefined,
    tag: <WppTag data-testid="status-tag-complete" label="Good" variant="positive" />,
  },
}

// checks the status and returns the corresponding icon and label from the STATUS object
export const statusChecker = (
  status:
    | JobsDetailData['status']
    | ProjectsUpdatePayload['status']
    | ImportsCreateDataV5['status']
    | VariantValidationStatusType,
  isProject?: boolean,
) => {
  switch (status?.toLocaleLowerCase()) {
    case 'draft':
      return STATUS.DRAFT
    case 'in_progress':
      return STATUS.IN_PROGRESS
    case 'ready_for_review':
      return STATUS.READY_FOR_REVIEW
    case 'ready_to_download':
      return STATUS.READY_TO_DOWNLOAD
    case 'submitted':
      return STATUS.SUBMITTED
    case 'completed':
      return isProject ? STATUS.PROJECT_COMPLETED : STATUS.COMPLETED
    case 'failed':
      return STATUS.FAILED
    case 'downloaded':
      return STATUS.DOWNLOADED
    case 'processing':
      return STATUS.PROCESSING
    case 'blocked':
      return STATUS.BLOCKED
    case 'executing':
      return STATUS.EXECUTING
    case 'importing':
      return STATUS.IMPORTING
    case 'incomplete':
      return STATUS.INCOMPLETE
    case 'complete':
      return STATUS.COMPLETE
    default:
      return STATUS.ERROR
  }
}
